import { ForumResponseModel } from "generated/api";
import React, { createContext, ReactNode, useContext, useMemo } from "react";
import {
  formatForumDate,
  getForumDateFormat,
  getForumDateTimeFormat,
  getForumTimeFormat,
} from "utils/dateUtils";

export type ForumDateFormatSource = Pick<ForumResponseModel, "countryCode" | "country"> | string | null | undefined;

type ForumDateContextValue = {
  dateFormatSource: ForumDateFormatSource;
  forumStartDate?: string;
};

const ForumDateFormatContext = createContext<ForumDateContextValue>({
  dateFormatSource: undefined,
  forumStartDate: undefined,
});

type ForumDateFormatProviderProps = {
  children: ReactNode;
  forum?: Pick<ForumResponseModel, "countryCode" | "country" | "startDate"> | null;
  countryCode?: string | null;
};

const ForumDateFormatProvider = ({ children, forum, countryCode }: ForumDateFormatProviderProps) => {
  const value = useMemo(
    () => ({
      dateFormatSource: forum?.countryCode || forum?.country ? forum : countryCode,
      forumStartDate: forum?.startDate,
    }),
    [countryCode, forum],
  );

  return <ForumDateFormatContext.Provider value={value}>{children}</ForumDateFormatContext.Provider>;
};

export const useForumDateFormatSource = () => useContext(ForumDateFormatContext).dateFormatSource;

export const useForumStartDate = () => useContext(ForumDateFormatContext).forumStartDate;

export const useForumDateFormat = () => getForumDateFormat(useForumDateFormatSource());

export const useForumDateTimeFormat = () => getForumDateTimeFormat(useForumDateFormatSource());

export const useForumTimeFormat = () => getForumTimeFormat();

export const useForumDateFormatter = () => {
  const source = useForumDateFormatSource();

  return useMemo(
    () => ({
      dateFormat: getForumDateFormat(source),
      dateTimeFormat: getForumDateTimeFormat(source),
      timeFormat: getForumTimeFormat(),
      formatDate: (
        date: object | string | null | undefined,
        options?: Parameters<typeof formatForumDate>[2],
      ) => formatForumDate(date, source, options),
    }),
    [source],
  );
};

export default ForumDateFormatProvider;
